import { Component, OnInit, ViewContainerRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { finalize, take } from 'rxjs/operators';
import { AppSelectOption } from '../../../../../../../_base-shared/contracts/common.interface';
import { IsDirtyGuardCheckInterface } from '../../../../../../../_base-shared/contracts/is-dirty-guard-check.interface';
import { LaravelResourceResponse } from '../../../../../../../_base-shared/contracts/laravel-response.interface';
import { Case } from '../../../../../../../_base-shared/models/Case/Case';
import { CaseAsset } from '../../../../../../../_base-shared/models/Case/CaseAsset';
import { Creditor } from '../../../../../../../_base-shared/models/Entity/Creditor';
import { CreditorService } from '../../../creditor/creditor.service';
import { FinancialOverviewService } from '../../../payment/financial-overview.service';
import { CaseAssetService } from '../../case-asset.service';
import { CaseService } from '../../case.service';

@Component({
  standalone:  false,
  selector:    'app-case-asset-editor',
  templateUrl: './case-asset-editor.component.html',
  styleUrls:   ['./case-asset-editor.component.scss'],
})
export class CaseAssetEditorComponent implements OnInit, IsDirtyGuardCheckInterface {
  public case: Case;
  public caseAssets: Array<CaseAsset>             = [];
  public isLoading                                = 0;
  public isSubmitting: boolean;
  public form: UntypedFormGroup;
  public serverResponse: LaravelResourceResponse;
  public creditors: Array<Creditor>               = [];
  public ownershipOptions: Array<AppSelectOption> = [];
  public homeTypes: Array<AppSelectOption>        = [];

  public propTypes: Array<AppSelectOption> = [
    {value: 'properties', label: this.translate.instant('CASES.editor.assets.table.properties')},
    {value: 'vehicles', label: this.translate.instant('CASES.editor.assets.table.vehicles')},
    {value: 'bank_accounts', label: this.translate.instant('CASES.editor.assets.table.bank_accounts')},
    {value: 'other', label: this.translate.instant('CASES.editor.assets.table.other')},
  ];
  public assets: Array<any>                = [];

  public totalNetBankAccounts = 0;
  public totalNetVehicles     = 0;
  public totalNetProperties   = 0;
  public totalNetOther        = 0;

  constructor(
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    private translate: TranslateService,
    private caseService: CaseService,
    private caseAssetService: CaseAssetService,
    private creditorService: CreditorService,
    private financialOverviewService: FinancialOverviewService,
    private viewContainerRef: ViewContainerRef,
  ) {
  }

  ngOnInit(): void {
    this.route.parent.paramMap.subscribe(params => {
      const caseId = +params.get('id');
      this.isLoading++;
      this.caseService.get(caseId).pipe(finalize(() => this.isLoading--)).subscribe(result => {
        this.case = result.data;
        this.isLoading++;
        this.caseAssetService.index(caseId).pipe(finalize(() => this.isLoading--)).subscribe(res => {
          this.caseAssets = res.data;
          this.buildForm(this.caseAssets);
        });
      });
    });
    this.fetchCreditors();
    this.buildSelectOptions();
  }


  public isComponentDirty(): boolean {
    return this.form?.dirty ?? false;
  }

  public isComponentValid(): boolean {
    return this.form?.valid ?? false;
  }

  public submitComponent(): void {
    this.submitForm();
  }

  public getRef(): ViewContainerRef {
    return this.viewContainerRef;
  }

  public submitForm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.caseAssetService.upsert(this.case.id, this.form.getRawValue())
      .pipe(finalize(() => this.isSubmitting = false))
      .subscribe({
        next: () => {
          this.form.markAsPristine();
          this.toastr.success(this.translate.instant('CASES.editor.assets.result.success'));
          this.assets = [];
          this.fetchAssets();
        },
        error: error => {
          this.serverResponse = error.error;
          this.toastr.error(this.translate.instant('CASES.editor.assets.result.error'), error.error.message);
        }
      });
  }

  public fetchAssets(): void {
    this.isLoading++;
    this.caseAssetService.index(this.case.id).pipe(finalize(() => this.isLoading--)).subscribe(
      result => {
        this.caseAssets = result.data;
        this.buildForm(this.caseAssets);
      },
      err => console.error(err),
    );
    this.updateTotalNet();
  }

  private fetchCreditors(): void {
    this.isLoading++;
    this.creditorService.index({select_all: 1, active: 1}).pipe(finalize(() => this.isLoading--)).subscribe(
      result => this.creditors = result.data,
      err => console.error(err),
    );
  }

  private buildSelectOptions(): void {
    this.ownershipOptions = [
      {value: 'applicant', label: this.translate.instant('CASE_CREDITOR.model.ownership.options.applicant_joint')},
      {value: 'partner', label: this.translate.instant('CASE_CREDITOR.model.ownership.options.partner')},
      {value: 'joint', label: this.translate.instant('CASE_CREDITOR.model.ownership.options.joint')},
    ];
    this.homeTypes        = [
      {value: 'home', label: this.translate.instant('CASE_ASSET.model.home_type.options.home')},
      {value: 'investment', label: this.translate.instant('CASE_ASSET.model.home_type.options.investment')},
      {value: 'land', label: this.translate.instant('CASE_ASSET.model.home_type.options.land')},
      {value: 'other', label: this.translate.instant('CASE_ASSET.model.home_type.options.other')},
    ];
  }

  private buildForm(caseAssets: Array<CaseAsset>): void {
    this.form = this.fb.group({
      type:       [null, Validators.required],
      balance:    [null, Validators.required],
      exposed:    [0],
      net_equity: [0],
      liquidable: [true],
    });

    caseAssets.forEach(caseAsset => {
      this.assets.push({
        id:         caseAsset.id,
        type:       this.propTypes.find(propType => propType.value === caseAsset.type).label,
        balance:    caseAsset.balance,
        exposed:    caseAsset.exposed,
        net_equity: caseAsset.balance - caseAsset.exposed,
        // liquidable: caseAsset.liquidable ? 'Yes' : 'No',
      });

    });
    this.form.get('balance').valueChanges.subscribe(() => {
      this.calculateEquity();
    });
    this.form.get('exposed').valueChanges.subscribe(() => {
      this.calculateEquity();
    });
    this.form.get('type').valueChanges.subscribe(() => {
      if (this.form.get('type').value === 'bank_accounts') {
        this.form.get('exposed').disable();
      } else {
        this.form.get('exposed').enable();
      }
    });
    this.updateTotalNet();
  }

  private calculateEquity(): void {
    const propValue              = this.form.get('balance').value;
    const propOutstandingFinance = this.form.get('exposed').value;
    const propNetEquity          = propValue - propOutstandingFinance;
    this.form.get('net_equity').patchValue(propNetEquity.toFixed(2), {emitEvent: false, onlySelf: true});
  }

  public destroyAsset(assetId: number): void {
    this.isLoading++;
    this.caseAssetService.destroy(this.case.id, assetId).pipe(finalize(() => this.isLoading--)).subscribe(
      () => {
        this.toastr.success(this.translate.instant('CASES.editor.assets.result.success'));
        this.assets = this.assets.filter(asset => asset.id !== assetId);
      },
      error => {
        this.serverResponse = error.error;
        this.toastr.error(this.translate.instant('CASES.editor.assets.result.error'), error.error.message);
      },
    );
  }

  private updateTotalNet(): void {
    const totalNet = this.totalNetBankAccounts + this.totalNetProperties + this.totalNetVehicles + this.totalNetOther;
    this.financialOverviewService.financialOverview$.pipe(take(1)).subscribe(overview => {
      if (overview) {
        overview.assets = totalNet;
        this.financialOverviewService.updateFinancialOverviewLocally(overview);
      }
    });
  }
}
